// Workspace conversations and Studio settings on VERA's server, so the same history and choices show in
// VERA for desktop, on the web and on any computer. This device keeps a copy for a fast start.
// The server stores them per company and per person, from the sign-in only (see the control plane's Conversations).

import type { ChatMessage } from "../api/vera";
import { vera } from "../api/vera";
import { auth, devMode } from "../auth/auth";
import { cpFetch } from "./controlPlane";

/** True when history and settings live on the server: live VERA with a real sign-in. */
export const serverHistory = () => vera.mode === "live" && !devMode;

export type ConvoMeta = { id: string; title: string; scope: string; group: string; updated: number };
const token = () => auth.getToken();
const path = (id: string) => `/v1/me/conversations/${encodeURIComponent(id)}`;

export const listConvos = () => cpFetch<{ conversations: ConvoMeta[] }>(token, "/v1/me/conversations").then((r) => r.conversations ?? []);
export const getConvo = (id: string) => cpFetch<ConvoMeta & { messages: ChatMessage[] }>(token, path(id));
export const saveConvo = (c: ConvoMeta, messages: ChatMessage[]) =>
  cpFetch<{ saved: boolean }>(token, path(c.id), { method: "POST", body: JSON.stringify({ title: c.title, scope: c.scope, group: c.group, updated: c.updated, messages }) });

// ---------- settings: privacy choices, connected Microsoft 365 apps, plug-in approvals ----------
type Settings = { values?: Record<string, string>; updated?: number };
let person: string | null = null;
const keys = (u: string): Record<string, string> => ({ consent: `vera-consent-${u}`, m365: `vera-m365-${u}`, plugins: "vera-plugins", google: `vera-google-on-${u}`, mailprov: `vera-mailprov-${u}`, home: `vera-home-${u}` });
const atKey = (u: string) => `vera-settings-at-${u}`;
const get = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const set = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };

function localValues(u: string) {
  const out: Record<string, string> = {};
  for (const [name, k] of Object.entries(keys(u))) { const v = get(k); if (v !== null) out[name] = v; }
  return out;
}

async function push(u: string) {
  await cpFetch(token, "/v1/me/settings", { method: "POST", body: JSON.stringify({ values: localValues(u), updated: Number(get(atKey(u)) || Date.now()) }) });
}

/**
 * After sign-in: brings this device and the server in line, whichever changed last wins.
 * Resolves true when this device's settings changed (so the app re-reads them).
 */
export async function syncSettings(u: string): Promise<boolean> {
  person = u;
  if (!serverHistory()) return false;
  const s = await cpFetch<Settings>(token, "/v1/me/settings");
  const localAt = Number(get(atKey(u)) || 0);
  const serverAt = Number(s.updated || 0);
  if (s.values && Object.keys(s.values).length && serverAt > localAt) {
    for (const [name, k] of Object.entries(keys(u))) if (typeof s.values[name] === "string") set(k, s.values[name]);
    set(atKey(u), String(serverAt));
    return true;
  }
  if (Object.keys(localValues(u)).length && (localAt > serverAt || !s.values || !Object.keys(s.values).length)) {
    if (!localAt) set(atKey(u), String(Date.now()));
    await push(u);
  }
  return false;
}

let timer = 0;
/** Call after saving a setting on this device: it goes to the server a moment later. */
export function settingsChanged() {
  const u = person;
  if (!u || !serverHistory()) return;
  set(atKey(u), String(Date.now()));
  window.clearTimeout(timer);
  timer = window.setTimeout(() => { void push(u).catch(() => { /* tried again on the next change or sign-in */ }); }, 800);
}
